import Vue from 'vue'

/**
 * Cookie / storage consent helper.
 *
 * GDPR / ePrivacy notes:
 * - Non-essential cookies (functional, analytics, marketing) must NOT be set
 *   before the user has given explicit, informed consent.
 * - Consent must be granular per category and revocable at any time.
 * - "Reject all" must be as easy as "Accept all".
 * - The decision is persisted in localStorage with a version and timestamp so
 *   that we can re-prompt the user if the policy changes.
 *
 * This module exposes:
 *   - getConsent()              -> stored decision or null
 *   - hasConsentDecision()      -> boolean
 *   - isCategoryAllowed(name)   -> boolean (NECESSARY is always true)
 *   - setConsent(payload)       -> persist and broadcast
 *   - acceptAll() / rejectAll() -> convenience helpers
 *   - revokeConsent()           -> wipe the decision (re-shows the banner)
 *   - openSettings()            -> request the banner UI to open the modal
 *   - onConsentChange(fn)       -> subscribe to changes (returns unsubscribe)
 *   - onOpenSettings(fn)        -> subscribe to "open settings" requests
 */

const STORAGE_KEY = 'cookie_consent'
const CURRENT_VERSION = 1
const LEGACY_BANNER_KEY = 'igrean' // dismiss-flag from the previous banner implementation

// One-time cleanup of the legacy banner key so it doesn't linger in
// localStorage of returning users.
if (typeof window !== 'undefined' && window.localStorage) {
  try {
    window.localStorage.removeItem(LEGACY_BANNER_KEY)
  } catch (e) { /* ignore */ }
}

export const ConsentCategories = Object.freeze({
  NECESSARY: 'necessary',
  FUNCTIONAL: 'functional',
  ANALYTICS: 'analytics',
})

const bus = new Vue()

// Push gtag consent update directly via the gtag() wrapper.
//
// Rationale: the Vue event bus above is reliable in dev, but in a code-split
// production bundle the `bus` singleton can end up duplicated across chunks
// (CookieWindow lives in a route chunk, the analytics listener lives in the
// main entry). When that happens the listener in `@/libs/analytics` never
// fires and `gtag('consent','update', ...)` is never called — leaving every
// subsequent hit pinned to `gcs=G100`. Calling directly here removes that
// cross-chunk dependency from the critical consent path; the bus is kept for
// UI reactivity only.
//
// IMPORTANT: the `consent` command is processed by gtag.js only when pushed
// via the gtag() function (which pushes `arguments`, an array-like object).
// A plain `dataLayer.push(['consent', 'update', {...}])` with a real Array is
// silently ignored. Hence we use `arguments` here, mirroring the bootstrap
// snippet in public/index.html.
function gtagPush() {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments)
}

function pushGtagConsentUpdate({ functional, analytics }) {
  gtagPush('consent', 'update', {
    analytics_storage: analytics ? 'granted' : 'denied',
    functionality_storage: functional ? 'granted' : 'denied',
    personalization_storage: functional ? 'granted' : 'denied',
  })
}

// Wipe `_ga*` cookies when analytics consent is revoked.
//
// Duplicated from `@/libs/analytics` on purpose: invoking the cleanup from
// here removes the dependency on the event-bus listener in analytics.js,
// which can sit in a different webpack chunk than the bus instance used by
// CookieWindow and silently fail to fire in production. Keeping the logic
// inline in the same module that mutates the consent decision guarantees
// the cookies are dropped synchronously with the user's revocation click.
function clearGaCookies() {
  if (typeof document === 'undefined' || typeof window === 'undefined') return
  const host = window.location.hostname
  const parts = host.split('.')
  const domains = []
  for (let i = 0; i < parts.length - 1; i += 1) {
    domains.push(`.${parts.slice(i).join('.')}`)
  }
  domains.push(host)

  document.cookie.split(';').forEach(rawCookie => {
    const eq = rawCookie.indexOf('=')
    const name = (eq > -1 ? rawCookie.slice(0, eq) : rawCookie).trim()
    if (!name) return
    if (!/^_ga/.test(name) && name !== '_gid' && name !== '_gat') return
    domains.forEach(domain => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`
    })
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
  })
}

function safeParse(raw) {
  try {
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

function readConsent() {
  if (typeof window === 'undefined' || !window.localStorage) return null
  const raw = window.localStorage.getItem(STORAGE_KEY)
  if (!raw) return null
  const parsed = safeParse(raw)
  if (!parsed || parsed.version !== CURRENT_VERSION) return null
  return parsed
}

export function getConsent() {
  return readConsent()
}

export function hasConsentDecision() {
  return readConsent() !== null
}

export function isCategoryAllowed(category) {
  if (category === ConsentCategories.NECESSARY) return true
  const c = readConsent()
  return !!(c && c[category] === true)
}

export function setConsent({ functional = false, analytics = false } = {}) {
  const payload = {
    version: CURRENT_VERSION,
    timestamp: new Date().toISOString(),
    necessary: true,
    functional: !!functional,
    analytics: !!analytics,
  }
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  }
  pushGtagConsentUpdate({ functional: payload.functional, analytics: payload.analytics })
  if (!payload.analytics) clearGaCookies()
  bus.$emit('change', payload)
  return payload
}

export function acceptAll() {
  return setConsent({ functional: true, analytics: true })
}

export function rejectAll() {
  return setConsent({ functional: false, analytics: false })
}

export function revokeConsent() {
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.removeItem(STORAGE_KEY)
  }
  pushGtagConsentUpdate({ functional: false, analytics: false })
  clearGaCookies()
  bus.$emit('change', null)
}

export function openSettings() {
  bus.$emit('open-settings')
}

export function onConsentChange(handler) {
  bus.$on('change', handler)
  return () => bus.$off('change', handler)
}

export function onOpenSettings(handler) {
  bus.$on('open-settings', handler)
  return () => bus.$off('open-settings', handler)
}
